import { cn } from '@sohanemon/utils';
import {
	DEFAULT_THEME_BY_SCHEME,
	THEME_DEFINITIONS,
	type ThemeMode,
} from '@sohanscript/theme';
import { SohanscriptLogo } from '@sohanscript/ui/sohanscript-logo';
import { TerminalFrame } from '@sohanscript/ui/terminal';
import type * as React from 'react';
import { ClickMock } from './click-mock';
import { ThemeBackdrop } from './theme-backdrop';

const PREVIEW_LINKS = ['works', 'notes', 'start', 'github'];

type ThemePreviewProps = {
	mode: ThemeMode;
	isActive: boolean;
};

export function ThemePreview({ mode, isActive }: ThemePreviewProps) {
	const preview = THEME_DEFINITIONS[mode].preview;
	const isAuto = mode === 'auto';
	const lightBackground =
		THEME_DEFINITIONS[DEFAULT_THEME_BY_SCHEME.light].preview.background;
	const darkBackground =
		THEME_DEFINITIONS[DEFAULT_THEME_BY_SCHEME.dark].preview.background;
	const previewBackground = isAuto
		? `linear-gradient(to right, ${lightBackground} 50%, ${darkBackground} 50%)`
		: preview.background;
	const style = {
		'--primary': preview.primary,
		'--muted-foreground': preview.muted,
		'--foreground': preview.foreground,
		'--background': preview.background,
		'--preview-background': preview.background,
		'--preview-muted': preview.muted,
		'--preview-foreground': preview.foreground,
		'--preview-primary': preview.primary,
		'--preview-accent': preview.accent,
		background: previewBackground,
	} as React.CSSProperties;

	if (!isActive) {
		return (
			<div
				className="isolate flex h-(--theme-picker-active-height) w-(--theme-picker-active-width) justify-center bg-(--preview-background)"
				style={style}
				aria-hidden="true"
			>
				<ThemeBackdrop
					mini
					className="z-10 absolute bottom-5 inset-x-0 mx-auto"
					mode={mode}
				/>
				<SohanscriptLogo className="relative z-10 top-[clamp(3.5rem,8vw,7rem)] size-[clamp(1.5rem,4vw,3rem)]" />
			</div>
		);
	}

	return (
		<TerminalFrame
			label="~/theme"
			compact
			className="absolute max-w-none isolate left-0 h-(--theme-picker-active-height) w-(--theme-picker-active-width) border-0 bg-(--preview-background)"
			style={style}
			aria-hidden="true"
		>
			<div
				className="relative isolate flex h-full flex-col gap-[clamp(0.15rem,1vw,0.75rem)] overflow-hidden bg-(--preview-background) p-[clamp(0.25rem,1.5vw,1rem)] font-mono text-[clamp(0.35rem,0.8vw,0.65rem)] leading-relaxed text-(--preview-foreground)"
				style={{ background: previewBackground }}
			>
				<ThemeBackdrop mode={mode} className="-z-10 absolute" />
				<div
					className={cn(
						'relative z-10 mx-auto flex size-[clamp(1.5rem,9vw,7rem)] shrink-0 items-center justify-center rounded-full border border-(--preview-primary) bg-(--preview-primary)/10 text-[clamp(0.6rem,2vw,1.5rem)] font-semibold tracking-[-0.12em] text-(--preview-primary) shadow-[0_0_20px_color-mix(in_srgb,var(--preview-primary)_25%,transparent)]',
						isAuto && 'mix-blend-difference',
					)}
				>
					<SohanscriptLogo className="size-5 lg:size-10" />
				</div>
				<div className={cn('space-y-0.5', isAuto && 'mix-blend-difference')}>
					<div className="text-(--preview-accent)">✓ profile loaded</div>
					<div className="text-(--preview-accent)">
						✓ stack: full-stack maker
					</div>
					<div className="text-(--preview-primary)">
						ⓘ available for new work
					</div>
				</div>
				<div
					className={cn(
						'flex items-center gap-1.5 text-(--preview-muted)',
						isAuto && 'mix-blend-difference',
					)}
				>
					<span>navigate</span>
					<div className="h-px flex-1 bg-(--preview-muted)" />
				</div>
				<div
					className={cn(
						'grid grid-cols-2 gap-x-3 gap-y-0.5 text-(--preview-primary) relative',
						isAuto && 'mix-blend-difference',
					)}
				>
					{PREVIEW_LINKS.map((link) => (
						<div key={`${mode}-${link}`} className="truncate">
							<span className="mr-1 text-(--preview-accent)">→</span>/{link}
						</div>
					))}
					<ClickMock className="absolute -bottom-9 right-16 z-10" />
				</div>
				<div
					className={cn(
						'mt-auto text-(--preview-primary)',
						isAuto && 'mix-blend-difference',
					)}
				>
					~{' '}
					<span className="text-(--preview-foreground)">
						make something memorable
					</span>
					_
				</div>
			</div>
		</TerminalFrame>
	);
}
